import { Close } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import {
    Alert,
    AlertColor,
    AlertTitle,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Snackbar,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import axios, { AxiosResponse } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supportApi } from '../../../shared';

export function Statusbar() {
    const { t } = useTranslation();
    const [status, setStatus] = useState<{ show: boolean; message: string; type: AlertColor | undefined }>({
        show: false,
        message: '',
        type: undefined,
    });
    const [blockingMessage, setBlockingMessage] = useState<{
        show: boolean;
        title: string;
        message: string;
    }>({ show: false, title: '', message: '' });

    const onSuccess = (response: AxiosResponse) => {
        if (!response.data || (!Array.isArray(response.data) && Object.keys(response.data).length === 0)) {
            Sentry.captureMessage('Empty response', { extra: { response } });
        }
        if (response.config.successMessage) {
            setStatus({ show: true, message: response.config.successMessage, type: 'success' });
        }
        return response;
    };
    const onError = useCallback(
        (error: any) => {
            if (error.response?.status === 401 || error.response?.status === 403) {
                window.location.href = '/auth/logout';
            }
            if (error.response?.status >= 500) {
                Sentry.captureException(error);
                if (error.config?.errorMessage) {
                    setStatus({ show: true, message: error.config.errorMessage, type: 'error' });
                } else {
                    setStatus({ show: true, message: t('tryAgainLater'), type: 'error' });
                }
            }
            if (!error.response) {
                setBlockingMessage({ show: true, title: t('networkErrorTitle'), message: t('networkErrorText') });
                Sentry.captureException(error);
            }
            return Promise.reject(error);
        },
        [t],
    );

    useEffect(() => {
        axios.interceptors.response.use(onSuccess, onError);
        supportApi.interceptors.response.use(onSuccess, onError);
    }, [onError]);

    const onCloseStatus = () => {
        setStatus({ ...status, show: false });
    };

    const onCloseBlockingMessage = () => {
        setBlockingMessage({ ...blockingMessage, show: false });
    };

    return (
        <>
            <Snackbar open={status.show} autoHideDuration={6000} onClose={onCloseStatus}>
                <Alert
                    severity={status.type}
                    action={
                        <IconButton size="small" aria-label="close" color="inherit" onClick={onCloseStatus}>
                            <Close fontSize="small" />
                        </IconButton>
                    }
                >
                    {status.type == 'error' && <AlertTitle>{t('something-went-wrong')}</AlertTitle>}
                    {status.message}
                </Alert>
            </Snackbar>
            <Dialog open={blockingMessage.show} onClose={onCloseBlockingMessage}>
                <DialogTitle>
                    <Grid container direction="row" alignItems="center">
                        <WarningIcon color="error" style={{ marginRight: '0.5em' }} />
                        {blockingMessage.title}
                    </Grid>
                </DialogTitle>
                <DialogContent sx={{ whiteSpace: 'pre-wrap' }}>{blockingMessage.message}</DialogContent>
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={onCloseBlockingMessage}>
                        {t('ok')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
